import { Clock, Shield, Swords, Trophy, Umbrella } from "lucide-solid";
import {
  Accessor,
  createMemo,
  createSignal,
  For,
  JSX,
  Match,
  Show,
  splitProps,
  Switch,
} from "solid-js";
import { DuelCard } from "~/components/duel-card";
import { DuelHistory } from "~/components/duel-history";
import { DriverCard, PinnedCategory } from "~/components/driver-card";
import { InstallButton } from "~/components/install-button";
import { PageContainer } from "~/components/layout/page";
import { ProgressPopup } from "~/components/progress-popup";
import { ProgressStrip } from "~/components/progress-strip";
import { RivalCarousel } from "~/components/rival-carousel";
import { TierBadge } from "~/components/tier-badge";
import { TournamentCard } from "~/components/tournament-card";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { playersApi, reportsApi, tournamentsApi, vouchersApi } from "~/lib/api";
import { useAuth } from "~/lib/auth";
import { FEATURES } from "~/lib/features";
import { CAR_CLASS_LABELS, RACE_TYPE_LABELS } from "~/lib/utils";

import { type CarClass, type PlayerProfile, type RaceType } from "system";
import { isTournamentEligible } from "~/lib/profile-utils";
import {
  createMutation,
  createQuery,
  useQueryClient,
} from "@tanstack/solid-query";
import { createFileRoute, Link, LinkProps } from "@tanstack/solid-router";
import { QueryKey } from "~/lib/query-keys";
import { requireFeature } from "~/lib/features";

export const Route = createFileRoute("/_authed/dashboard")({
  beforeLoad: () => requireFeature("dashboard"),
  component: Dashboard,
});

function Dashboard() {
  const { state } = useAuth();

  const myProfile = createQuery(() => ({
    queryKey: [QueryKey.Profile, state.user?.id],
    queryFn: () => playersApi.get(state.user!.id),
    enabled: !!state.user?.id,
    staleTime: 60_000,
  }));

  const tournaments = createQuery(() => ({
    queryKey: [QueryKey.Tournaments, "upcoming"],
    queryFn: () => tournamentsApi.list({ status: "upcoming" }),
    reconcile: "id",
  }));

  const splitTournaments = createMemo(() => {
    const profile = myProfile.data;
    const ts = tournaments.data ?? [];
    const eligible: typeof ts = [];
    const ineligible: typeof ts = [];
    for (const t of ts) {
      const ok = !!profile && isTournamentEligible(profile, t);
      (ok ? eligible : ineligible).push(t);
    }
    return {
      eligible: eligible.slice(0, 3),
      ineligible: ineligible.slice(0, 3),
    };
  });

  const [pinnedCategory, setPinnedCategory] = createSignal<
    PinnedCategory | undefined
  >();

  return (
    <PageContainer>
      <InstallButton variant="ghost" size="sm" dismissible />

      {/* ── Progression hero ──────────────────────────────────────────── */}
      <Show
        when={myProfile.data}
        fallback={
          <div class="min-h-[200px] bg-surface-2 rounded-xl animate-pulse" />
        }
      >
        {(p) => (
          <>
            <CardHeroPair profile={p()} pinnedCategory={pinnedCategory} />
            <ProgressPopup profile={p()} />
          </>
        )}
      </Show>

      {/* Active duels */}
      <Show when={(myProfile.data?.activeDuels.length ?? 0) > 0}>
        <section class="space-y-3">
          <SectionHeader title="Active Duels" icon="swords" href="/duels" />
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <For each={myProfile.data?.activeDuels}>
              {(duel) => <DuelCard duel={duel} />}
            </For>
          </div>
        </section>
      </Show>

      <Show when={myProfile.data}>
        {(p) => <RivalCarousel myProfile={p()} />}
      </Show>

      <Show when={myProfile.data}>
        {(p) => (
          <ProgressStrip profile={p()} setPinnedCategory={setPinnedCategory} />
        )}
      </Show>

      {/* ── Upcoming tournaments ───────────────────────────────────────── */}
      <Show
        when={
          splitTournaments().eligible.length +
            splitTournaments().ineligible.length >
          0
        }
      >
        <section class="space-y-3">
          <SectionHeader
            title="Upcoming Tournaments"
            icon="trophy"
            href="/tournaments"
          />

          {/* Eligible — rich cards */}
          <Show when={splitTournaments().eligible.length > 0}>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <For each={splitTournaments().eligible}>
                {(t) => (
                  <TournamentCard
                    tournament={t}
                    userId={state.user?.id}
                    eligible={true}
                  />
                )}
              </For>
            </div>
          </Show>

          {/* Ineligible — compact list */}
          <Show when={splitTournaments().ineligible.length > 0}>
            <div class="space-y-1.5">
              <For each={splitTournaments().ineligible}>
                {(t) => (
                  <Link
                    to="/tournaments/$tournamentId"
                    params={{ tournamentId: t.id }}
                    class="flex items-center gap-3 p-3 rounded-lg hover:bg-surface-3 transition-colors opacity-50"
                  >
                    <Badge variant="pending">
                      {t.participants?.length ?? 0} joined
                    </Badge>
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-semibold text-text-base truncate">
                        {t.name}
                      </p>
                      <p class="text-xs text-text-faint">
                        {RACE_TYPE_LABELS[t.raceType as RaceType]} ·{" "}
                        {CAR_CLASS_LABELS[t.carClass as CarClass]}
                      </p>
                    </div>
                    <div class="flex flex-wrap gap-1">
                      <For each={t.eligibleTiers}>
                        {(tier) => <TierBadge tier={tier} size="sm" />}
                      </For>
                    </div>
                  </Link>
                )}
              </For>
            </div>
          </Show>
        </section>
      </Show>

      {/* ── Vacation vouchers ──────────────────────────────────────────── */}
      <Show when={myProfile.data}>
        {(p) => <VoucherSection profile={p()} />}
      </Show>

      {/* ── Recent results ─────────────────────────────────────────────── */}
      <Show when={FEATURES.matchHistory ? myProfile.data : undefined}>
        {(p) => (
          <Show when={p().recentDuels.length > 0}>
            <section class="space-y-3">
              <SectionHeader
                title="Recent Results"
                to="/profile/$username"
                params={{ username: p().username }}
                search={{ tab: "history" }}
                icon={<Clock size={14} class="text-text-faint shrink-0" />}
              />

              <DuelHistory
                duels={p().recentDuels.slice(0, 5)}
                viewerId={p().id}
              />
            </section>
          </Show>
        )}
      </Show>

      {/* ── Judge / admin panel ────────────────────────────────────────── */}
      <Show when={state.user?.role !== "member"}>
        <JudgeSection />
      </Show>
    </PageContainer>
  );
}

function SectionHeader(
  props: {
    title: string;
    icon?: "swords" | "trophy" | JSX.Element;
  } & LinkProps,
) {
  const [{ title, icon }, link] = splitProps(props, ["title", "icon"]);
  return (
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <Switch fallback={icon}>
          <Match when={icon === "swords"}>
            <Swords size={14} class="text-brand shrink-0" />
          </Match>
          <Match when={icon === "trophy"}>
            <Trophy size={14} class="text-brand shrink-0" />
          </Match>
        </Switch>
        <span class="font-sans text-sm font-semibold uppercase tracking-display text-text-muted">
          {title}
        </span>
      </div>
      <Show when={props.href || props.to}>
        <Link {...link}>
          <Button variant="ghost" size="sm">
            View all
          </Button>
        </Link>
      </Show>
    </div>
  );
}

function CardHeroPair(props: {
  profile: PlayerProfile;
  pinnedCategory?: Accessor<PinnedCategory | undefined>;
}) {
  return (
    <div class="relative">
      <Link
        to="/profile/$username"
        params={{ username: props.profile.username }}
        search={{ tab: undefined }}
        class="relative z-10"
      >
        <DriverCard
          profile={props.profile}
          pinnedCategory={props.pinnedCategory?.()}
        />
      </Link>
      {/* <Show when={preview()}>
        {(p) => (
          <Portal>
            <Link
              to="/rankings"
              search={{
                rt: topRanking()?.raceType,
                cc: topRanking()?.carClass,
                q: undefined,
              }}
              class="absolute top-20 left-0 w-full z-20 translate-x-[calc(100%-8px)] sm:translate-x-[calc(100%-16px)] xl:translate-x-[calc(100%-32px)] hover:translate-x-[calc(100%-144px)] opacity-30 hover:opacity-100! transition-transform,opacity duration-300"
            >
              <div class="[&_a]:pointer-events-none">
                <DriverCard profile={p()} />
              </div>
            </Link>
          </Portal>
        )}
      </Show> */}
    </div>
  );
}

function VoucherSection(props: { profile: PlayerProfile }) {
  const { state } = useAuth();
  const qc = useQueryClient();

  const activate = createMutation(() => ({
    mutationFn: () => vouchersApi.activateAny(),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: [QueryKey.Profile, state.user?.id] }),
  }));

  return (
    <Show
      when={props.profile.availableVouchers > 0 && !props.profile.vacationUntil}
    >
      <section class="space-y-3">
        <div class="flex items-center gap-2">
          <Umbrella size={14} class="text-brand shrink-0" />
          <span class="font-sans text-sm font-semibold uppercase tracking-display text-text-muted">
            Vacation Vouchers
          </span>
        </div>
        <Card class="flex items-center gap-3 p-4">
          <div class="flex-1 min-w-0">
            <p class="text-sm text-text-base">
              {props.profile.availableVouchers} voucher
              {props.profile.availableVouchers !== 1 ? "s" : ""} available
            </p>
            <p class="text-xs text-text-muted mt-0.5">
              Activating grants 1 week of protection from challenges. Cancelled
              if you issue a challenge first.
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => activate.mutate()}
            disabled={activate.isPending}
            class="shrink-0"
          >
            {activate.isPending ? "Activating..." : "Take a break"}
          </Button>
        </Card>
        <Show when={activate.isError}>
          <p class="text-xs text-red-400">{(activate.error as any)?.message}</p>
        </Show>
      </section>
    </Show>
  );
}

function JudgeSection() {
  const reports = createQuery(() => ({
    queryKey: [QueryKey.Reports, "open"],
    queryFn: () => reportsApi.list({ status: "open" }),
  }));

  return (
    <Show when={(reports.data?.length ?? 0) > 0}>
      <section class="space-y-3">
        <SectionHeader title="Open Reports" icon="swords" href="/admin" />
        <Card class="flex items-center gap-3 p-4">
          <Shield size={16} class="text-brand shrink-0" />
          <span class="text-sm text-text-base">
            {reports.data?.length} open report
            {(reports.data?.length ?? 0) !== 1 ? "s" : ""} awaiting review
          </span>
          <Link to="/admin" class="ml-auto">
            <Button variant="ghost" size="sm">
              Review
            </Button>
          </Link>
        </Card>
      </section>
    </Show>
  );
}
